import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, ScanLine, Car, User, DollarSign, Trash2, Plus, CheckCircle2, Wrench } from "lucide-react";

/**
 * New Job Wizard (direct approval, no quote):
 *   1) Paste VIN → decode + auto-detect ADAS calibrations via the full engine
 *   2) Review/edit line items + customer + salesperson + technician
 *   3) Create → generates Calibration Report + Job + Invoice atomically.
 * The technician sees the job in the Work Order Queue and the invoice
 * appears in Billing → Customer tab (or Body Shop when the toggle is on).
 */
export default function NewJobDirect() {
  const navigate = useNavigate();

  const [vinInput, setVinInput] = useState("");
  const [vinLoading, setVinLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [vehicle, setVehicle] = useState({ year: "", make: "", model: "" });
  const [repairType, setRepairType] = useState("glass_only");
  const [collisionDamage, setCollisionDamage] = useState(false);
  const [postAlignment, setPostAlignment] = useState(false);
  const [lines, setLines] = useState([]);

  const [customer, setCustomer] = useState({
    customer_name: "", customer_email: "", customer_phone: "", customer_address: "",
    is_body_shop: false, linked_customer_id: "", linked_body_shop_id: "",
    salesperson_id: "", salesperson_name: "", commission_pct: 5,
    technician_id: "", scheduled_at: "", tax_pct: 0, notes: "",
  });

  const [contactOptions, setContactOptions] = useState([]);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [salespeople, setSalespeople] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        // Union crm_contacts + master_customers + master_body_shops so no
        // customer is missing from the picker even if the collections drift.
        const [crmRes, masterCustRes, shopsRes] = await Promise.all([
          api.get("/crm/contacts?type=customer"),
          api.get("/master/customers"),
          api.get("/master/body-shops"),
        ]);
        const items = [];
        const seen = new Set();
        const add = (v) => { if (v && v.id && !seen.has(v.id)) { seen.add(v.id); items.push(v); } };
        (shopsRes.data || []).filter((s) => !s.archived).forEach((s) => add({
          id: s.id, kind: "body_shop", name: s.company_name,
          email: s.email, phone: s.phone,
          address: [s.address, s.city, s.state, s.zip_code].filter(Boolean).join(", "),
        }));
        (crmRes.data || []).forEach((c) => add({
          id: c.id, kind: "customer", name: c.name,
          email: c.email, phone: c.phone, address: c.address,
        }));
        (masterCustRes.data || []).filter((c) => !c.archived).forEach((c) => add({
          id: c.id, kind: "customer",
          name: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || "—",
          email: c.email, phone: c.phone,
          address: [c.address, c.city, c.state, c.zip_code].filter(Boolean).join(", "),
        }));
        setContactOptions(items);
      } catch { /* noop */ }
      try {
        let users = [];
        try { users = (await api.get("/users/roster")).data || []; }
        catch { users = (await api.get("/admin/users")).data || []; }
        setSalespeople(users.filter((u) => (u.role || "").toLowerCase() === "sales"));
        setTechnicians(users.filter((u) => (u.role || "").toLowerCase() === "technician"));
      } catch { /* noop */ }
    })();
  }, []);

  // ---- VIN decode (chains into ADAS analyze for smoother 1-step flow)
  const decodeVin = async (opts = {}) => {
    const vin = (vinInput || "").trim().toUpperCase();
    if (vin.length < 11) return toast.error("VIN precisa ter no mínimo 11 caracteres");
    setVinLoading(true);
    try {
      const r = await api.get(`/vin/decode/${vin}`);
      const d = r.data || {};
      setVehicle({ year: d.year || "", make: d.make || "", model: d.model || "" });
      toast.success(`Decoded · ${d.year || ""} ${d.make || ""} ${d.model || ""}`.trim());
      if (opts.chain !== false) {
        // Auto-run ADAS analysis right after decode succeeds
        await analyzeAdas({ silent: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "VIN decode falhou");
    } finally {
      setVinLoading(false);
    }
  };

  // ---- Analyze VIN → suggested ADAS operations
  const analyzeAdas = async (opts = {}) => {
    const vin = (vinInput || "").trim().toUpperCase();
    if (vin.length < 11) return toast.error("Cole o VIN primeiro");
    setAnalyzing(true);
    try {
      const r = await api.post("/jobs/analyze-vin", {
        vin,
        vehicle_year: vehicle.year ? parseInt(vehicle.year) : null,
        vehicle_make: vehicle.make || null,
        vehicle_model: vehicle.model || null,
        repair_type: repairType,
        collision_damage: collisionDamage,
        post_wheel_alignment: postAlignment,
      });
      const d = r.data || {};
      // Preserve any user-created lines above the newly-detected ones
      const detected = (d.operations || []).map((op, i) => ({
        line_no: lines.length + i + 1,
        op_key: op.op_key, op_label: op.op_label,
        cal_type: op.cal_type, unit_price: op.price,
        amount: op.price, hours: op.hours || 0,
        category: op.category, confidence: op.confidence, selected: true,
      }));
      setLines([...lines, ...detected]);
      if (d.vehicle && (!vehicle.year || !vehicle.make || !vehicle.model)) {
        setVehicle({
          year: d.vehicle.year || vehicle.year,
          make: d.vehicle.make || vehicle.make,
          model: d.vehicle.model || vehicle.model,
        });
      }
      if (!opts.silent) {
        toast.success(`Detectadas ${detected.length} operações ($${d.total?.toFixed(2)})`);
      } else if (detected.length > 0) {
        toast.success(`${detected.length} operações ADAS sugeridas · $${d.total?.toFixed(2)}`);
      }
    } catch (e) {
      if (!opts.silent) toast.error(e?.response?.data?.detail || "Análise falhou");
    } finally {
      setAnalyzing(false);
    }
  };

  const addLine = () => setLines([...lines, {
    line_no: lines.length + 1, op_key: "", op_label: "",
    cal_type: "static", unit_price: 0, amount: 0, hours: 0, selected: true,
  }]);
  const updateLine = (idx, key, val) => {
    const next = [...lines];
    next[idx][key] = ["unit_price", "amount", "hours"].includes(key) ? parseFloat(val) || 0 : val;
    if (key === "unit_price") next[idx].amount = parseFloat(val) || 0;
    setLines(next);
  };
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const toggleLine = (idx) => {
    const next = [...lines];
    next[idx].selected = !next[idx].selected;
    setLines(next);
  };

  const subtotal = lines.filter((l) => l.selected !== false).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const tax = subtotal * (parseFloat(customer.tax_pct) || 0) / 100;
  const total = subtotal + tax;

  const submit = async () => {
    const vin = (vinInput || "").trim().toUpperCase();
    if (vin.length < 11) return toast.error("VIN obrigatório");
    if (!customer.customer_name) return toast.error("Nome do cliente obrigatório");
    const selected = lines.filter((l) => l.selected !== false);
    if (selected.length === 0) return toast.error("Adicione ao menos uma operação");
    setSaving(true);
    try {
      const r = await api.post("/jobs/direct", {
        vin,
        vehicle_year: vehicle.year ? parseInt(vehicle.year) : null,
        vehicle_make: vehicle.make || null,
        vehicle_model: vehicle.model || null,
        ...customer,
        vehicle_year_int: vehicle.year ? parseInt(vehicle.year) : null,
        salesperson_id: customer.salesperson_id || null,
        technician_id: customer.technician_id || null,
        commission_pct: parseFloat(customer.commission_pct) || 5,
        tax_pct: parseFloat(customer.tax_pct) || 0,
        lines: selected.map((l) => ({
          op_key: l.op_key || null,
          op_label: l.op_label,
          cal_type: l.cal_type || "static",
          unit_price: parseFloat(l.unit_price) || 0,
          amount: parseFloat(l.amount) || 0,
          hours: parseFloat(l.hours) || 0,
        })),
      });
      const d = r.data;
      toast.success(`Job criado · WO #${d.calibration_report_id?.slice(0, 8) || d.job_id?.slice(0, 8)} · Invoice ${d.invoice_number}`, {
        description: `A Work Order (${d.calibration_report_id ? "ADAS report" : "job"}) está na fila do técnico. Invoice em Billing → ${customer.is_body_shop ? "Body Shop" : "Customer"}.`,
        duration: 6000,
      });
      // Navigate to Work Order (technician's execution view) if we have one, else to Job
      if (d.calibration_report_id) {
        navigate(`/work-orders/${d.calibration_report_id}`);
      } else {
        navigate(`/jobs/${d.job_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up max-w-6xl" data-testid="new-job-direct-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Jobs · New Work Order</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">Novo Job</h1>
        <p className="text-sm text-slate-400 mt-1.5">Cole o VIN → o motor analisa ADAS + trim + gera preços do Rate Table. Revise, ajuste cliente/técnico, e crie — a ordem vai ao técnico e a invoice já vai pra Billing.</p>
      </div>

      {/* 1) VIN + Analysis */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-4" data-testid="vin-analysis-card">
        <div className="flex items-center gap-2 mb-1">
          <ScanLine className="w-5 h-5 text-blue-400" />
          <h2 className="font-display text-xl font-medium">1. VIN + Análise ADAS</h2>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="vin-input"
            placeholder="Cole o VIN aqui (17 caracteres)"
            value={vinInput}
            onChange={(e) => setVinInput(e.target.value.toUpperCase())}
            className="bg-[#030712] border-slate-800 font-mono"
          />
          <Button onClick={decodeVin} disabled={vinLoading || analyzing} className="bg-blue-600 hover:bg-blue-500" data-testid="decode-vin-btn">
            {(vinLoading || analyzing) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Decode + Analisar ADAS
          </Button>
          <Button onClick={() => analyzeAdas()} disabled={analyzing || vinLoading} variant="outline" className="border-slate-700" data-testid="analyze-adas-btn" title="Re-run ADAS analysis with current toggles">
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-analisar"}
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Ano</Label>
            <Input value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" /></div>
          <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Marca</Label>
            <Input value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" /></div>
          <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Modelo</Label>
            <Input value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" /></div>
        </div>
        <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-800">
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Tipo de Reparo</Label>
            <select value={repairType} onChange={(e) => setRepairType(e.target.value)} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-lg p-2 text-sm" data-testid="repair-type-select">
              <option value="glass_only">Glass Only (Windshield)</option>
              <option value="body_shop_collision">Body Shop / Collision</option>
              <option value="mechanical">Mechanical / Service</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 self-end pb-2">
            <input type="checkbox" checked={collisionDamage} onChange={(e) => setCollisionDamage(e.target.checked)} className="w-4 h-4 accent-blue-500" />
            Collision damage
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 self-end pb-2">
            <input type="checkbox" checked={postAlignment} onChange={(e) => setPostAlignment(e.target.checked)} className="w-4 h-4 accent-blue-500" />
            Post wheel alignment
          </label>
        </div>
      </div>

      {/* 2) Operations detected */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-4" data-testid="operations-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-400" />
            <h2 className="font-display text-xl font-medium">2. Operações ({lines.filter((l) => l.selected !== false).length} selecionadas)</h2>
          </div>
          <Button size="sm" variant="outline" onClick={addLine} className="border-slate-700" data-testid="add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" />Adicionar linha</Button>
        </div>
        {lines.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-800 rounded-lg p-6 text-center">
            Nenhuma operação ainda. Clique em <strong className="text-blue-300">Analisar ADAS</strong> ou <strong className="text-blue-300">Adicionar linha</strong>.
          </div>
        )}
        {lines.map((l, i) => (
          <div key={i} className={`grid grid-cols-12 gap-2 items-center py-2 border-b border-slate-800/50 last:border-0 ${l.selected === false ? "opacity-40" : ""}`} data-testid={`line-row-${i}`}>
            <input type="checkbox" checked={l.selected !== false} onChange={() => toggleLine(i)} className="col-span-1 w-4 h-4 accent-blue-500 ml-3" />
            <Input value={l.op_label} onChange={(e) => updateLine(i, "op_label", e.target.value)} placeholder="Descrição" className="col-span-5 bg-[#030712] border-slate-800 text-sm h-8" />
            <select value={l.cal_type} onChange={(e) => updateLine(i, "cal_type", e.target.value)} className="col-span-2 bg-[#030712] border border-slate-800 rounded-md p-1 text-xs h-8">
              <option value="static">Static</option>
              <option value="dynamic">Dynamic</option>
              <option value="dual">Dual</option>
            </select>
            <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} placeholder="Price" className="col-span-2 bg-[#030712] border-slate-800 text-sm h-8 font-mono" />
            <button onClick={() => removeLine(i)} className="col-span-1 text-slate-500 hover:text-red-400 justify-self-center"><Trash2 className="w-4 h-4" /></button>
            <div className="col-span-1 text-right text-emerald-400 font-mono text-sm">${(parseFloat(l.amount) || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>

      {/* 3) Customer + assignments */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-4" data-testid="customer-card">
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-emerald-400" />
          <h2 className="font-display text-xl font-medium">3. Cliente + Atribuições</h2>
          {customer.is_body_shop && <span className="text-[10px] uppercase tracking-wider font-bold text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-full px-2 py-0.5">Body Shop</span>}
        </div>

        <div className="rounded-lg border border-slate-800 bg-[#030712] p-2">
          <button type="button" onClick={() => setContactPickerOpen((v) => !v)} className="w-full text-left text-xs text-slate-400 hover:text-slate-200 flex items-center justify-between px-2 py-1.5" data-testid="direct-contact-picker">
            {customer.linked_customer_id || customer.linked_body_shop_id ? "✓ Contato existente selecionado" : "Buscar cliente ou body shop já cadastrado..."}
          </button>
          {contactPickerOpen && (
            <div className="mt-2 border-t border-slate-800 pt-2">
              <Input autoFocus placeholder="Buscar..." value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} className="bg-[#030712] border-slate-800 h-8 text-sm mb-2" />
              <div className="max-h-56 overflow-auto space-y-1">
                {contactOptions.filter((c) => !contactSearch.trim() || (c.name || "").toLowerCase().includes(contactSearch.toLowerCase())).slice(0, 30).map((c) => (
                  <button key={`${c.kind}-${c.id}`} type="button" onClick={() => {
                    setCustomer({
                      ...customer,
                      customer_name: c.name, customer_email: c.email || "", customer_phone: c.phone || "", customer_address: c.address || "",
                      is_body_shop: c.kind === "body_shop",
                      linked_customer_id: c.kind === "customer" ? c.id : "",
                      linked_body_shop_id: c.kind === "body_shop" ? c.id : "",
                    });
                    setContactPickerOpen(false); setContactSearch("");
                    toast.success(`${c.kind === "body_shop" ? "Body Shop" : "Cliente"} selecionado`);
                  }} className="w-full text-left rounded-md border border-slate-800 bg-[#0B1120] hover:border-blue-500/40 px-2.5 py-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${c.kind === "body_shop" ? "bg-blue-500/20 text-blue-300" : "bg-emerald-500/20 text-emerald-300"}`}>{c.kind === "body_shop" ? "Body Shop" : "Cliente"}</span>
                      <span className="text-slate-200 font-medium">{c.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 cursor-pointer" data-testid="direct-is-body-shop-label">
          <input type="checkbox" checked={customer.is_body_shop} onChange={(e) => setCustomer({ ...customer, is_body_shop: e.target.checked, linked_customer_id: "" })} className="w-4 h-4 accent-blue-500" />
          <span className="text-xs text-slate-200">Cliente é um <strong>Body Shop</strong> — será replicado nas duas listas</span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Nome *</Label>
            <Input data-testid="direct-customer-name" value={customer.customer_name} onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value, linked_customer_id: "", linked_body_shop_id: "" })} className="mt-1 bg-[#030712] border-slate-800" /></div>
          <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Email</Label>
            <Input type="email" value={customer.customer_email} onChange={(e) => setCustomer({ ...customer, customer_email: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" /></div>
          <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Telefone</Label>
            <Input value={customer.customer_phone} onChange={(e) => setCustomer({ ...customer, customer_phone: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" /></div>
          <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Endereço</Label>
            <Input value={customer.customer_address} onChange={(e) => setCustomer({ ...customer, customer_address: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" /></div>
        </div>

        <div className="grid grid-cols-4 gap-3 pt-3 border-t border-slate-800">
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Vendedor</Label>
            <select value={customer.salesperson_id} onChange={(e) => {
              const sp = salespeople.find((s) => s.id === e.target.value);
              setCustomer({ ...customer, salesperson_id: e.target.value, salesperson_name: sp?.name || "" });
            }} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-lg p-2 text-sm" data-testid="salesperson-select">
              <option value="">— Nenhum —</option>
              {salespeople.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Comissão %</Label>
            <Input type="number" value={customer.commission_pct} onChange={(e) => setCustomer({ ...customer, commission_pct: e.target.value })} className="mt-1 bg-[#030712] border-slate-800 font-mono" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Técnico</Label>
            <select value={customer.technician_id} onChange={(e) => setCustomer({ ...customer, technician_id: e.target.value })} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-lg p-2 text-sm" data-testid="technician-select">
              <option value="">— Fila aberta —</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Agendado para</Label>
            <Input type="datetime-local" value={customer.scheduled_at} onChange={(e) => setCustomer({ ...customer, scheduled_at: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" />
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Notas</Label>
          <Textarea value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" placeholder="Detalhes internos para o técnico..." />
        </div>
      </div>

      {/* 4) Summary + submit */}
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 flex items-center justify-between" data-testid="submit-card">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Total</div>
          <div className="font-display text-3xl font-semibold text-emerald-400 font-mono mt-1">${total.toFixed(2)}</div>
          <div className="text-xs text-slate-400 mt-1">Subtotal ${subtotal.toFixed(2)} · Tax ${tax.toFixed(2)}</div>
        </div>
        <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 h-12 px-6 text-base font-semibold" data-testid="create-direct-job-btn">
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
          Criar Job + Invoice + Work Order
        </Button>
      </div>
    </div>
  );
}
